'use client';

import { useGSAP } from '@gsap/react';
import clsx from "clsx";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from "next/image";
import { useRef, useMemo } from 'react';

import Reveal from "@/components/animation/reveal";
import Cta from "@/components/cta/cta";
import Section from "@/components/section";

// Split array into columns of fixed size (2 images per column)
const splitIntoColumns = (arr, itemsPerCol = 2) => {
  const numCols = Math.ceil(arr.length / itemsPerCol);
  return Array.from({ length: numCols }, (_, i) =>
    arr.slice(i * itemsPerCol, (i + 1) * itemsPerCol)
  );
};

export default function AboutCollection({ images, link, text, title }) {
  const columns = useMemo(() => splitIntoColumns(images, 2), [images]);
  const containerRef = useRef(null);
  const trackRef = useRef(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      // Entrance animation for columns
      const columnElements = trackRef.current.children;
      const xOffsets = [300, 200, 100, 0, -100, -200, -300];

      Array.from(columnElements).forEach((col, i) => {
        gsap.set(col, { y: 500, x: xOffsets[i] || 0 });
      });

      ScrollTrigger.create({
        trigger: containerRef.current,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          gsap.to(columnElements, {
            y: 0,
            x: 0,
            duration: 1.5,
            ease: 'power3.out',
            stagger: {
              from: 'center',
              amount: 0.15,
            },
          });
        },
      });
    });
  }, []);

  return (
    <Section theme="main-light" className="pt-29.25 lg:pt-32.5">
      <div className="grid-7 items-center mx-auto">
        <Reveal className="col-span-full lg:[grid-column:2/7] max-w-130 mx-auto lg:max-w-162.25">
          <h2 className="heading-3-small max-w-80 mx-auto text-center lg:max-w-none lg:heading-3">{title}</h2>
          <p className="mt-6 body-1-small text-center text-pretty xl:body-2 lg:mt-8 lg:max-w-[82.74%] lg:mx-auto">{text}</p>
          <Cta href={link.href} className="w-fit mx-auto mt-8 cta-big">{link.label}</Cta>
        </Reveal>
      </div>

      <div ref={containerRef} className="overflow-hidden mt-10 lg:mt-16" aria-hidden="true">
        <div ref={trackRef} className="relative left-1/2 -translate-x-1/2 flex w-max mx-auto gap-x-4.5 lg:gap-x-9 will-change-transform">
          {columns.map((column, colIndex) => (
            <div key={colIndex} className={clsx(
              "shrink-0 flex-col gap-5 w-[40.8vw] lg:flex lg:gap-8 lg:w-[18.75vw] will-change-transform",
              (colIndex === 0 || colIndex === columns.length - 1) ? "hidden" : "flex",
              colIndex === 0 && "lg:mt-10",
              colIndex === 1 && "mt-8 lg:mt-22.5",
              colIndex === 3 && "mt-12.25 lg:mt-21",
              colIndex === 4 && "mt-5 lg:mt-5.5",
              colIndex === 5 && "mt-8 lg:mt-25.5",
              colIndex === 6 && "lg:mt-10"
            )}>
              {column.map((item) => (
                <Image unoptimized={true} key={item._uid} {...item} alt={item.alt || ''} loading="lazy" className={clsx(
                  item.className,
                  colIndex === 0 && '-rotate-10',
                  colIndex === 1 && '-rotate-[6.6deg]',
                  colIndex === 2 && '-rotate-[3.1deg]',
                  colIndex === 4 && 'rotate-[3.1deg]',
                  colIndex === 5 && 'rotate-[6.6deg]',
                  colIndex === 6 && 'rotate-10'
                )} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}
